import { useState, type FormEvent } from 'react';

import type { StockLocation } from '../api/inventory.ts';
import { dayWithYear } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ INV-A09 at v1.986 (venue slice 3B-1): the tenant's locations, which is home today, which
// becomes home later, and — for someone who manages inventory — making another home from a day.
// Only the depot or a venue that keeps stock can be home (the API says which, `canBeHome`); the
// database refuses anything else, so the button is simply not offered there.

export function LocationsSection({
  locations,
  mayManage,
  saving,
  error,
  onAdd,
}: {
  locations: readonly StockLocation[];
  mayManage: boolean;
  saving: boolean;
  error: Error | null;
  onAdd: (locationId: string, from: string) => Promise<unknown>;
}) {
  const [choosing, setChoosing] = useState<string | null>(null);
  const [from, setFrom] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!choosing || !from) return;
    await onAdd(choosing, from);
    setChoosing(null);
    setFrom('');
  }

  return (
    <section className="flex flex-col gap-2" aria-labelledby="locations">
      <h2 id="locations" className="section-title">
        Locations
      </h2>
      <p className="text-sm text-ink-soft">
        Home is where new stock arrives and what a job draws on last.
      </p>
      <ul className="flex flex-col gap-2">
        {locations.map((l) => {
          const offer = mayManage && l.canBeHome && !l.home && !l.homeFrom;
          return (
            <li key={l.id} className="flex flex-col gap-1">
              <span className="flex flex-wrap items-center gap-2">
                <span className="font-semibold text-ink">{l.name}</span>
                {l.home ? <span className="tag tag-accent">Home</span> : null}
                {l.homeFrom ? (
                  <span className="tag">Home from {dayWithYear(l.homeFrom)}</span>
                ) : null}
                {offer && choosing !== l.id ? (
                  <Button
                    variant="quiet"
                    aria-label={`Make ${l.name} home`}
                    onClick={() => setChoosing(l.id)}
                  >
                    Make home
                  </Button>
                ) : null}
              </span>
              {choosing === l.id ? (
                <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
                  <Field
                    label="From"
                    type="date"
                    required
                    value={from}
                    onChange={(e) => setFrom(e.currentTarget.value)}
                  />
                  <Button type="submit" disabled={saving}>
                    Save
                  </Button>
                  <Button variant="quiet" onClick={() => setChoosing(null)}>
                    Cancel
                  </Button>
                </form>
              ) : null}
            </li>
          );
        })}
      </ul>
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </section>
  );
}
